import { ButtonList, DropdownButton, Tooltip } from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import { accessControl } from 'lib-shared-access-control';
import {
  ConversationsChannels,
  Message,
  MessageEmailRecipient,
  MetaConversation,
} from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import { User } from 'lib-shared-types-api';
import { ReactElement } from 'react';
import * as React from 'react';

import type { PhoneNumber } from 'apis/numbers';
import { ComposeEmailModalFormValues } from 'components/shared/composeEmail/components/ComposeEmailModal';
import { useCommandHandler } from 'utils/commands';

import ConversationSummaryHelper from '../../../app/utils/conversationSummaryHelper';
import { getSubject } from '../../../app/utils/conversationTitle';
import { getMessagesTranscript } from '../../../app/utils/messages';
import { isNumberValid } from '../../../app/utils/telephonyHelpers';
import ConversationForm from '../containers/conversationForm';
import NoteForm from '../containers/noteForm';
import PhoneForm from '../containers/phoneForm';
import type { EmailMessageType } from '../reducers/emailMessage';
import { getLatestFacebookConversation } from '../selectors';
import { canClaimConversation } from '../utils/canClaimConversation';
import { canTakeOverConversation } from '../utils/canTakeOverConversation';
import { getReplyWindowExpiresOnFromConversation } from '../utils/getReplyWindowExpiresOnFromConversation';

import {
  getRequesterWithDisplayName,
  isAbandonedConversation,
  isCallbackConsumed,
  isConversationPhoneCall,
  isConversationTextBased,
  isEndUserConnected,
  shouldShowTranscript,
} from './ReplySection.utils';
import { ReplyStatus } from './replyStatus';
import { RespondTabs } from './respondTabs';
import { ReviewStatus } from './reviewStatus';
import { isReviewChannel } from './utils/reviewChannel';
import { getLastReviewReply } from './utils/reviewReply';

import '../styles/reply-section.less';

export type PropsT = {
  isAssignedToMe: boolean;
  csid: number;
  isClosed: boolean;
  isResolved: boolean;
  channel: ConversationsChannels;
  metaConversation: MetaConversation;
  replyAreaHeight: number;
  initialValues?: string;
  rewrittenHtmlEmailMessages: Array<EmailMessageType>;
  updateReplyAreaHeight: (payload: { value: number }) => Promise<any>;
  hasFFWriteHTMLEmail: boolean;
  requester?: User;
  telephonyMakeCall: (payload: {
    userId: string;
    toNumber: string;
    fromNumber: string;
  }) => Promise<any>;
  telephonyFromNumber: PhoneNumber;

  composeEmailOpenModal: (payload?: {
    initialValues: ComposeEmailModalFormValues;
  }) => void;
  conversationClaim: ({ csid }: { csid: number }) => Promise<any>;
  selectFromEmail: any;
  updateCurrentTabStatus: (payload: {
    csid: number;
    tabStatus: 'reply' | 'note';
  }) => void;
  latestMessengerConversation: ReturnType<typeof getLatestFacebookConversation>;
  tempCurrentTab?: 'reply' | 'note';
};

type StateT = {
  currentTab: 'reply' | 'note';
  containerHeight: number;
  pristine: boolean;
  showReviewReplyInfo: boolean;
};

const MIN_TEXT_CONTAINER_HEIGHT = 200;

function listContactDetails(
  supportEmail: string,
  authors: User[],
  linebreak: string,
) {
  let authorString = `---------- Contact details ----------${linebreak}`;
  if (!authors) {
    return null;
  }
  const authorsArray = Object.values(authors);
  if (!Array.isArray(authorsArray)) {
    return null;
  }

  const contacts = authorsArray.filter((author) => author.type === 'contact');
  contacts.push({ email: supportEmail } as User);
  contacts.forEach((author: User) => {
    const name = author.name || '';
    const email = author.email || '';
    authorString += `${name} ${email}${linebreak}`;
  });
  return authorString;
}

function listCcBccDetails(messages: Message[], linebreak: string) {
  return [
    { label: 'CC', selector: ({ cc_list_v2 }: Message) => cc_list_v2 },
    { label: 'BCC', selector: ({ bcc_list_v2 }: Message) => bcc_list_v2 },
  ].reduce((acc, { label, selector }) => {
    const emails = messages
      .reduce<
        MessageEmailRecipient[]
      >((recipients, message) => [...recipients, ...(selector(message) ?? [])], [])
      .map((recipient) => recipient.email);

    return emails.length
      ? `${acc}---------- ${label} details ----------${linebreak}${emails.join(
          ', ',
        )}${linebreak}`
      : acc;
  }, '');
}

export default class ReplySection extends React.Component<PropsT, StateT> {
  containerRef: React.RefObject<HTMLDivElement>;

  constructor(props: PropsT) {
    super(props);

    this.state = {
      currentTab: 'reply',
      containerHeight: props.replyAreaHeight,
      pristine: true,
      showReviewReplyInfo: false,
    };

    this.containerRef = React.createRef();
  }

  componentDidMount(): void {
    // Restore tab from tempCurrentTab if available (when returning to the conversation)
    const restoredTab = this.props.tempCurrentTab ?? 'reply';
    this.setState({
      currentTab: restoredTab,
      showReviewReplyInfo: !!getLastReviewReply(this.props.metaConversation),
    });
  }

  componentDidUpdate(prevProps: PropsT) {
    // We flip the pristine to true when changing csid focus.
    if (this.props.csid !== prevProps.csid) {
      // Restore tab from tempCurrentTab or default to 'reply'
      const restoredTab = this.props.tempCurrentTab ?? 'reply';
      this.setState({
        currentTab: restoredTab,
        pristine: true,
        showReviewReplyInfo: !!getLastReviewReply(this.props.metaConversation),
      });
    } else if (
      // Enable review info if a new reply was added
      isReviewChannel(this.props.channel) &&
      !this.state.showReviewReplyInfo &&
      prevProps.metaConversation.messages.length <
        this.props.metaConversation.messages.length
    ) {
      this.setState({
        showReviewReplyInfo: !!getLastReviewReply(this.props.metaConversation),
      });
    }
  }

  handleTabChange = (value: StateT['currentTab']) => {
    this.setState(
      {
        currentTab: value,
        pristine: false,
      },
      () => {
        if (value === 'note') {
          ampli.conversationInternalNoteEditorOpened();
        }

        this.props.updateCurrentTabStatus({
          csid: this.props.csid,
          tabStatus: value,
        });
      },
    );
  };

  @boundMethod
  async handleClaim(): Promise<void> {
    const { conversationClaim, metaConversation } = this.props;
    const showTakeoverButton = canTakeOverConversation(
      metaConversation.conversation,
      metaConversation.isAssignedToMe,
    );

    await conversationClaim({ csid: this.props.csid });

    ampli.conversationClaimed({
      conversationId: this.props.csid,
      type: showTakeoverButton ? 'takeOver' : 'claim',
      location: 'conversation',
      channel:
        this.props.metaConversation.conversation?.initial_channel || undefined,
      language: this.props.metaConversation.conversation?.attributes?.language,
      tags: this.props.metaConversation.conversation.tags,
    });
  }

  startResize = (event: React.PointerEvent): void => {
    if (!this.containerRef.current) return;

    event.preventDefault();
    const startY = event.clientY;
    const startHeight = this.containerRef.current.offsetHeight;

    const moveHandler = (event: PointerEvent) => {
      const newHeight = Math.max(
        startHeight + (startY - event.clientY),
        MIN_TEXT_CONTAINER_HEIGHT,
      );

      this.setState({
        containerHeight: newHeight,
      });
    };

    const dropHandler = () => {
      window.removeEventListener('pointermove', moveHandler);
      window.removeEventListener('pointerup', dropHandler);
      this.props.updateReplyAreaHeight({
        value: this.state.containerHeight,
      });
    };

    window.addEventListener('pointermove', moveHandler);
    window.addEventListener('pointerup', dropHandler);
  };

  generateCallFunction() {
    const { requester, telephonyMakeCall, telephonyFromNumber } = this.props;

    if (
      requester &&
      requester.phone_number &&
      telephonyFromNumber &&
      telephonyFromNumber.e164_format
    ) {
      if (isNumberValid({ phoneNumber: requester.phone_number }) === true) {
        return () => {
          telephonyMakeCall({
            userId: requester.id,
            toNumber: requester.phone_number as string,
            fromNumber: telephonyFromNumber.e164_format,
          });
        };
      }
    }
    return null;
  }

  @boundMethod
  sideConversationClickHandler() {
    const { initial_channel, attributes, id } =
      this.props.metaConversation.conversation;
    const initialValues = {
      parentConversation: id,
      linkConversationType: 'side',
    } as ComposeEmailModalFormValues;

    if (initial_channel && ['email', 'contactform'].includes(initial_channel)) {
      initialValues.fromEmails = attributes?.integration_email as any;
    }

    this.props.composeEmailOpenModal({ initialValues });

    ampli.conversationActionsMenuOptionClicked({
      option: 'side_conversation',
    });
  }

  @boundMethod
  followupConversationClickHandler() {
    const {
      metaConversation: {
        conversation: { id, tags },
      },
      requester,
    } = this.props;

    const initialValues = {
      parentConversation: id,
      linkConversationType: 'follow_up',
      toUser: [{ user: getRequesterWithDisplayName(requester) }],
      tags,
      ...this.getTranscriptFormData(),
    } as ComposeEmailModalFormValues;

    this.props.composeEmailOpenModal({ initialValues });

    ampli.conversationActionsMenuOptionClicked({
      option: 'followup_conversation',
    });
  }

  @boundMethod
  emailForwardConversationClickhandler() {
    const {
      metaConversation: {
        conversation: { id },
      },
      composeEmailOpenModal,
    } = this.props;

    const initialValues = {
      parentConversation: id,
      linkConversationType: 'email_forward',
      ...this.getTranscriptFormData(),
    } as ComposeEmailModalFormValues;

    composeEmailOpenModal({ initialValues });

    ampli.conversationActionsMenuOptionClicked({
      option: 'send_transcript',
    });
  }

  /**
   * Checks if the reply window for the current conversation has expired.
   *
   * @returns True if the reply window has expired, false otherwise
   */
  isReplyWindowExpired(): boolean {
    const replyWindowExpiresOn = getReplyWindowExpiresOnFromConversation(
      this.props.metaConversation,
    );

    if (!replyWindowExpiresOn) {
      return false;
    }

    return Date.now() >= replyWindowExpiresOn.getTime();
  }

  @boundMethod
  getExplanation(helper: ConversationSummaryHelper): JSX.Element | undefined {
    if (this.isReplyWindowExpired()) {
      if (this.props.channel === 'facebookmessenger') {
        return (
          <>
            Messages can no longer be sent because{' '}
            {helper.getRequster() || 'Contact'} has not replied in the last 24h.{' '}
            <a
              style={{ textDecoration: 'none' }}
              target="_blank"
              rel="noopener noreferrer"
              href="https://support.dixa.help/en/articles/215"
            >
              Read more
            </a>
          </>
        );
      }

      return (
        <>
          Messages can no longer be sent because the reply window has expired.
        </>
      );
    }

    const isOutboundAllowed = helper.getOutboundEnabled() !== false;

    if (!isOutboundAllowed) {
      return <>Outbound messages are disabled.</>;
    }

    return undefined;
  }

  @boundMethod
  hideReviewInfo(): void {
    this.setState({ showReviewReplyInfo: false });
  }

  getTranscriptFormData(): ComposeEmailModalFormValues {
    const {
      metaConversation,
      rewrittenHtmlEmailMessages,
      hasFFWriteHTMLEmail,
      selectFromEmail: fromEmails,
    } = this.props;

    const subject = getSubject(
      metaConversation.conversation && metaConversation.conversation.attributes
        ? metaConversation.conversation.attributes
        : null,
    );

    const { transcript, attachments, authors } = getMessagesTranscript(
      metaConversation,
      rewrittenHtmlEmailMessages,
      hasFFWriteHTMLEmail,
    );

    const linebreak = `<br /><br />`;
    const conversation = metaConversation.conversation;
    const isInboundConversation = conversation.direction === 'inbound';
    const supportEmail = isInboundConversation
      ? (conversation.attributes?.to_email as string)
      : (conversation.attributes?.from_email as string);
    const listContactLead = listContactDetails(
      supportEmail,
      authors,
      linebreak,
    );
    const listCcBccLead = listCcBccDetails(
      metaConversation.messages,
      linebreak,
    );
    const customerLead = listContactLead
      ? `${listContactLead}${listCcBccLead}`
      : '';

    return {
      subject: subject ? `Fwd: ${subject}` : undefined,
      message: `${linebreak}${linebreak}${customerLead}${linebreak}---------- Transcript message ---------${linebreak}${transcript}`,
      messageContentType: hasFFWriteHTMLEmail ? 'html' : 'markdown',
      attachments,
      fromEmails,
    };
  }

  generateComposeFunction() {
    const { requester } = this.props;
    const requesterWithDisplayname = getRequesterWithDisplayName(requester);

    if (requesterWithDisplayname?.email) {
      return () => {
        this.props.composeEmailOpenModal({
          initialValues: {
            toUser: [{ user: requesterWithDisplayname }],
          },
        });
      };
    }
    return null;
  }

  renderTabContent() {
    const {
      isClosed,
      isResolved,
      isAssignedToMe,
      channel,
      latestMessengerConversation,
      metaConversation,
    } = this.props;

    const showTakeoverButton = canTakeOverConversation(
      metaConversation.conversation,
      metaConversation.isAssignedToMe,
    );
    const showClaimButton = showTakeoverButton
      ? false
      : canClaimConversation(
          metaConversation.conversation,
          metaConversation.isAssignedToMe,
          latestMessengerConversation,
        );

    const { currentTab } = this.state;
    const helper = new ConversationSummaryHelper(metaConversation);
    let tabContent: ReactElement | null = null;

    const showPhoneCallOver =
      (!isClosed &&
        ['pstnphone', 'voicemail'].includes(channel) &&
        !isEndUserConnected(metaConversation)) ||
      (!isClosed &&
        ['callback'].includes(channel) &&
        isCallbackConsumed(metaConversation));
    const showPhoneForm =
      (!isClosed &&
        ['pstnphone', 'voicemail'].includes(channel) &&
        isEndUserConnected(metaConversation)) ||
      (!isClosed &&
        ['callback'].includes(channel) &&
        !isCallbackConsumed(metaConversation));

    const isPublicReply = helper.getAttributes().is_public_content === 'true';
    const isAbandoned = isAbandonedConversation(metaConversation);
    const showAsClosed =
      isClosed &&
      !['email', 'contactform', 'sms'].includes(channel) &&
      !isReviewChannel(channel);
    const showAsResolved = isResolved && !showAsClosed;
    const contactCallback = {
      onCall: this.generateCallFunction() as () => void,
      onCompose: this.generateComposeFunction() as () => void,
    };

    if (currentTab === 'reply') {
      const isNotYours =
        !showAsClosed && !isConversationPhoneCall(channel) && !isAssignedToMe
          ? true
          : !isAssignedToMe;

      const outboundAllowed =
        helper.getOutboundEnabled() !== false && !this.isReplyWindowExpired();

      let customText;
      if (
        showPhoneCallOver &&
        !(
          (isAbandoned || isClosed === false) &&
          channel !== 'callback' &&
          !isAssignedToMe
        )
      ) {
        customText = 'The phone call has ended';
      } else if (!outboundAllowed && isNotYours) {
        customText =
          'You can assign yourself, but you will still not be able to reply';
      }

      tabContent = (
        <ReplyStatus
          explanation={this.getExplanation(helper)}
          customText={customText}
          className="reply-section__status"
          showClaimButton={showClaimButton}
          showTakeoverButton={showTakeoverButton}
          onClaim={this.handleClaim}
          isClosed={showAsClosed}
          isResolved={showAsResolved}
          isNotYours={isNotYours}
          {...contactCallback}
        />
      );

      if (
        isConversationTextBased(channel) &&
        isAssignedToMe &&
        !showAsClosed &&
        !showAsResolved &&
        outboundAllowed
      ) {
        const hasIntegrationSetupAccess = accessControl.hasAccess(
          'settings.integration.channel.setup',
        );

        tabContent = this.state.showReviewReplyInfo ? (
          <ReviewStatus onClick={this.hideReviewInfo} />
        ) : (
          <ConversationForm
            key={this.props.csid}
            className="reply-section__tab"
            isPublicReply={isPublicReply}
            hasIntegrationSetupAccess={hasIntegrationSetupAccess}
          />
        );
      }

      if (showPhoneForm) {
        tabContent = <PhoneForm className="reply-section__tab" />;
      }
    } else if (currentTab === 'note') {
      tabContent = <NoteForm />;
    }

    return tabContent;
  }

  conversationSupportsFollowup(): boolean {
    const { channel } = this.props;
    const channelsWithFollowupSupport: ConversationsChannels[] = [
      'adachat',
      'digitalgeniuschat',
      'kindlychat',
      'genericapimessaging',
      'messenger', //new dixa messenger
      'widgetchat', // old dixa messenger
    ];
    return (
      channelsWithFollowupSupport.includes(channel) || isReviewChannel(channel)
    );
  }

  render() {
    const { isClosed, metaConversation, channel } = this.props;
    const { currentTab, containerHeight } = this.state;
    const hasReplyAccess = accessControl.hasAccess('conversation.reply');

    const respondTabsChildren =
      hasReplyAccess && !metaConversation.parentConversation ? (
        <DropdownButton
          buttonProps={{ variant: 'tertiary' }}
          onToggle={(isOpen) => isOpen && ampli.conversationActionsMenuOpened()}
          dropdown={
            <div style={{ width: '300px' }}>
              <ButtonList>
                {shouldShowTranscript(channel) && (
                  <ButtonList.Item
                    icon="transcript"
                    onClick={this.emailForwardConversationClickhandler}
                  >
                    Send transcript
                  </ButtonList.Item>
                )}

                <ButtonList.Item
                  icon="mail"
                  onClick={this.sideConversationClickHandler}
                >
                  Side conversation
                </ButtonList.Item>

                {this.conversationSupportsFollowup() && (
                  <Tooltip
                    content="Once the conversation is closed, you can start a follow-up conversation"
                    disabled={isClosed}
                    wrap="div"
                  >
                    <ButtonList.Item
                      icon="chat"
                      data-testid="widget-chat"
                      isDisabled={!isClosed}
                      onClick={this.followupConversationClickHandler}
                    >
                      Follow-up conversation
                    </ButtonList.Item>
                  </Tooltip>
                )}
              </ButtonList>
            </div>
          }
        >
          Actions
        </DropdownButton>
      ) : null;

    return (
      <div
        className="reply-section"
        style={{ height: containerHeight }}
        ref={this.containerRef}
      >
        <ReplySectionCommandHandler
          currentTab={currentTab}
          onChange={(tab) => this.setState({ currentTab: tab })}
        />

        <RespondTabs
          value={currentTab}
          className="reply-section__tabs"
          onResize={this.startResize}
          onChange={this.handleTabChange as (value: string) => void}
          showSendTranscript={false}
          onTranscriptClick={this.emailForwardConversationClickhandler}
        >
          {respondTabsChildren as ReactElement}
        </RespondTabs>

        {this.renderTabContent()}
      </div>
    );
  }
}

type CommandHandlerProps = Pick<StateT, 'currentTab'> & {
  onChange: (tab: StateT['currentTab']) => void;
};

const ReplySectionCommandHandler = ({
  onChange,
  currentTab,
}: CommandHandlerProps): null => {
  const tab = React.useRef(currentTab);

  React.useEffect(() => {
    tab.current = currentTab;
  }, [currentTab]);

  useCommandHandler({
    name: 'conversation:internalNote',
    actionFn: () => {
      const newTab: StateT['currentTab'] =
        tab.current === 'note' ? 'reply' : 'note';
      tab.current = newTab;
      onChange(newTab);
    },
  });

  return null;
};
